import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Sheet, CaptionText } from '@paypalcorp/pp-react';
import GivingSummaryHeader from '../givingSummaryHeader/givingSummaryHeader';
import GivingSummarySection from '../givingSummarySection/givingSummarySection';
import GivingSummaryError from '../givingSummaryError/givingSummaryError';
import {
  getCurrentYear,
  getYearStartDate,
  getYearEndDate,
} from '../../lib/dateUtils';
import styles from './givingSummary.less';
import * as analytics from '../../lib/analytics';
import isEmpty from 'lodash/isEmpty';

const PRODUCT_TYPE = {
  CROWDFUNDING_CAMPAIGNS: 'CROWDFUNDING_CAMPAIGNS',
  CROWDFUNDING_CAMPAIGNS_CHARITIES: 'CROWDFUNDING_CAMPAIGNS_CHARITIES',
};

const currentYear = getCurrentYear();
const lastYear = currentYear - 1;

function initializer({ isEngaged, charitiesSupport, fundraiserSupport, i18n }) {
  const title = isEngaged
    ? i18n('label.summary-title')
    : i18n('label.summary-title-current', { currentYear });
  const subtitle = i18n('label.summary-subtitle');
  const currentYearRange = {
    displayTitle: currentYear.toString(),
    startDate: getYearStartDate(currentYear),
    endDate: getYearEndDate(currentYear),
  };
  const lastYearRange = {
    displayTitle: lastYear.toString(),
    startDate: getYearStartDate(lastYear),
    endDate: getYearEndDate(lastYear),
  };
  const donationAmount = {
    label: i18n('label.summary-donated'),
    value: 0,
  };
  const donationStats = [
    {
      label: i18n('label.summary-causes'),
      value: 0,
    },
    {
      label: i18n('label.summary-donations'),
      value: 0,
    },
  ];
  const raisedAmount = {
    label: i18n('label.summary-raised'),
    value: 0,
  };
  const raisedStats = [
    {
      label: i18n('label.summary-fundraisers'),
      value: 0,
    },
  ];
  const charitiesCTAs = charitiesSupport
    ? [
        {
          id: 'SFC',
          title: i18n('label.cta-set-favorite-charity'),
          icon: 'donate',
          url: '/fundraiser/set-favorite-charity',
        },
        {
          id: 'DPC',
          title: i18n('label.cta-popular-charity'),
          icon: 'general-support',
          url:
            '/fundraiser/hub?keywords=&page=1&pageSize=24&sortBy=trending&type=charities',
        },
      ]
    : [];
  const fundraiserCTAs = fundraiserSupport
    ? [
        {
          id: 'STFR',
          title: i18n('label.cta-start-fundraiser'),
          icon: 'fundraiser',
          url: '/pools/c/create/types',
        },
      ]
    : [];

  return {
    heading: title,
    subheading: subtitle,
    dateRanges: [currentYearRange, lastYearRange],
    suggestedActions: [...charitiesCTAs, ...fundraiserCTAs],
    donationDetails: {
      amount: donationAmount,
      stats: donationStats,
      suggestedActions: [],
    },
    raisedDetails: {
      amount: raisedAmount,
      stats: raisedStats,
      suggestedActions: [],
    },
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'set_donation_details': {
      return {
        ...state,
        donationDetails: {
          ...state.donationDetails,
          ...action.payload,
        },
      };
    }
    case 'set_raised_details': {
      return {
        ...state,
        raisedDetails: {
          ...state.raisedDetails,
          ...action.payload,
        },
      };
    }
    case 'set_heading_title': {
      return {
        ...state,
        heading: action.payload,
      };
    }
    default: {
      throw Error('Unknown action: ' + action.type);
    }
  }
}

const defaultParameters = {
  sampling_days: '0',
  realtime: true,
};

const GivingSummary = ({
  isEngaged = true,
  charitiesSupport = true,
  fundraiserSupport = true,
  preferredCharity,
  isCountrySetPreferredSupported,
  isSheetOpen,
  closeSheet,
  defaultFPTITags,
  i18n,
  csrf,
  cdnHost,
}) => {
  const initializerArg = {
    isEngaged,
    charitiesSupport,
    fundraiserSupport,
    i18n,
  };
  const [insights, setInsights] = useState({});
  const [summary, dispatch] = useReducer(reducer, initializerArg, initializer);
  const [timeFrame, setTimeFrame] = useState(summary.dateRanges[0]);
  const [isDonationLoading, setIsDonationLoading] = useState(isEngaged);
  const [isDonationError, setIsDonationError] = useState(false);
  const [isRaisedLoading, setIsRaisedLoading] = useState(isEngaged);
  const [isRaisedError, setIsRaisedError] = useState(false);
  const [isCharityLoading, setIsCharityLoading] = useState(isEngaged);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(
    isEngaged && fundraiserSupport
  );
  const [showPreviousYear, setShowPreviousYear] = useState(true);

  useEffect(() => {
    if (isSheetOpen) {
      analytics.instance.recordEvent(
        analytics.events.DW_GIVING_IMPACT_SUMMARY_SCREEN_SHOWN,
        {
          ...defaultFPTITags,
          e: 'im',
        }
      );
    }
  }, [isSheetOpen]);

  const preferredCharityCTA = preferredCharity
    ? {
        id: 'GFC',
        title: i18n('label.cta-give-favorite-charity'),
        subtitle: preferredCharity.name,
        logo: preferredCharity.logo_url,
        url: `/fundraiser/charity/${preferredCharity.nonprofit_id}`,
      }
    : null;

  const setFavoriteCharityCTA = isCountrySetPreferredSupported
    ? {
        id: 'SFC',
        title: i18n('label.cta-set-favorite-charity'),
        icon: 'donate',
        url: '/fundraiser/set-favorite-charity',
      }
    : null;

  const trendingCharityCTA = {
    id: 'DPC',
    title: i18n('label.cta-popular-charity'),
    icon: 'general-support',
    url:
      '/fundraiser/hub?keywords=&page=1&pageSize=24&sortBy=trending&type=charities',
  };

  const fetchUserInsights = (payload) => {
    return fetch('/fundraiser/insights/user', {
      method: 'POST',
      body: JSON.stringify({ ...payload, ...defaultParameters, _csrf: csrf }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  const requestsParams = [
    {
      type: 'SENT',
      start_date: timeFrame.startDate,
      end_date: timeFrame.endDate,
    },
    {
      type: 'SENT',
      start_date: summary.dateRanges[1].startDate,
      end_date: summary.dateRanges[1].endDate,
    },
    {
      type: 'RECEIVED',
      start_date: timeFrame.startDate,
      end_date: timeFrame.endDate,
      product_types: [
        PRODUCT_TYPE.CROWDFUNDING_CAMPAIGNS,
        PRODUCT_TYPE.CROWDFUNDING_CAMPAIGNS_CHARITIES,
      ],
    },
    {
      type: 'RECEIVED',
      start_date: summary.dateRanges[1].startDate,
      end_date: summary.dateRanges[1].endDate,
      product_types: [
        PRODUCT_TYPE.CROWDFUNDING_CAMPAIGNS,
        PRODUCT_TYPE.CROWDFUNDING_CAMPAIGNS_CHARITIES,
      ],
    },
  ];

  useEffect(() => {
    if (!isDonationLoading || !isRaisedLoading || !isSheetOpen) {
      return;
    }

    Promise.all(requestsParams.map(fetchUserInsights))
      .then((responses) => {
        const errors = responses.filter((response) => response.status >= 400);
        if (errors.length > 0) {
          throw errors.map((response) => new Error(response.statusText));
        }
        const jsonResponses = responses.map((response) => response.json());
        return Promise.all(jsonResponses);
      })
      .then((data) => {
        const allData = {
          current: {
            sent: data[0].data.userInsights,
            received: data[2].data.userInsights,
          },
          previous: {
            sent: data[1].data.userInsights,
            received: data[3].data.userInsights,
          },
        };

        setInsights(allData);

        if (
          allData.previous.sent.totalProducts +
            allData.previous.received.totalProducts ===
          0
        ) {
          setShowPreviousYear(false);
          dispatch({
            type: 'set_heading_title',
            payload: i18n('label.summary-title-current', { currentYear }),
          });
        }
      })
      .catch((errors) => {
        if (errors[0] || errors[1]) {
          setIsDonationError(true);
        }
        if (errors[2] || errors[2]) {
          setIsRaisedError(true);
        }
      })
      .finally(() => {
        setIsDonationLoading(false);
        setIsRaisedLoading(false);
      });
  }, [isDonationLoading, isRaisedLoading, isSheetOpen]);

  useEffect(() => {
    if (!isCharityLoading || !isSheetOpen) {
      return;
    }

    fetch('/fundraiser/insights/user/charity')
      .then((response) => {
        if (response.status >= 400) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(({ data: { supportedCharity } }) => {
        const supportedCharityCTA = supportedCharity
          ? {
              id: 'SCA',
              title: i18n('label.cta-support-charity-again'),
              subtitle: supportedCharity.name,
              logo: supportedCharity.logo_url,
              url: `/fundraiser/charity/${supportedCharity.nonprofit_id}`,
            }
          : null;

        let suggestedCTA = setFavoriteCharityCTA;

        if (preferredCharityCTA) {
          suggestedCTA = preferredCharityCTA;
        } else if (supportedCharityCTA) {
          suggestedCTA = supportedCharityCTA;
        }

        if (!suggestedCTA) {
          suggestedCTA = trendingCharityCTA;
        }

        dispatch({
          type: 'set_donation_details',
          payload: {
            suggestedActions: [suggestedCTA],
          },
        });
      })
      .catch(() => {
        const suggestedCTA = preferredCharityCTA || setFavoriteCharityCTA;
        dispatch({
          type: 'set_donation_details',
          payload: {
            suggestedActions: [suggestedCTA],
          },
        });
      })
      .finally(() => {
        setIsCharityLoading(false);
      });
  }, [isCharityLoading, isSheetOpen]);

  useEffect(() => {
    if (!isCampaignsLoading || !isSheetOpen) {
      return;
    }
    fetch('/fundraiser/campaigns-list')
      .then((response) => {
        if (response.status >= 400) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(({ data: { campaignList } }) => {
        const fundraiser = campaignList.find(
          (campaign) => campaign.status === 'ACTIVE'
        );
        const suggestedCTA = fundraiser
          ? {
              id: 'SHFR',
              title: i18n('label.cta-share-fundraiser'),
              subtitle: fundraiser.event_title,
              logo: JSON.parse(fundraiser.template_data || '{}')
                .background_image,
              url: fundraiser.event_url,
            }
          : {
              id: 'STFR',
              title: i18n('label.cta-start-fundraiser'),
              icon: 'fundraiser',
              url: '/pools/c/create/types',
            };
        dispatch({
          type: 'set_raised_details',
          payload: {
            suggestedActions: [suggestedCTA],
          },
        });
      })
      .catch(() => {
        dispatch({
          type: 'set_raised_details',
          payload: {
            suggestedActions: [
              {
                id: 'STFR',
                title: i18n('label.cta-start-fundraiser'),
                icon: 'fundraiser',
                url: '/pools/c/create/types',
              },
            ],
          },
        });
      })
      .finally(() => {
        setIsCampaignsLoading(false);
      });
  }, [isCampaignsLoading, isSheetOpen]);

  useEffect(() => {
    if (isEmpty(insights)) {
      return;
    }

    const sentUserInsigths =
      timeFrame.displayTitle === summary.dateRanges[0].displayTitle
        ? insights.current.sent
        : insights.previous.sent;
    const receivedUserInsights =
      timeFrame.displayTitle === summary.dateRanges[0].displayTitle
        ? insights.current.received
        : insights.previous.received;

    const {
      totalDonationInPrimaryCurrency: sentTotalDonationInPrimaryCurrency = {},
      totalProducts: sentTotalProducts = 0,
    } = sentUserInsigths;

    dispatch({
      type: 'set_donation_details',
      payload: {
        amount: {
          label: i18n('label.summary-donated'),
          value: sentTotalDonationInPrimaryCurrency.value || 0,
        },
        stats: [
          {
            label: i18n('label.summary-causes'),
            value: sentTotalProducts,
          },
          {
            label: i18n('label.summary-donations'),
            value: sentTotalDonationInPrimaryCurrency.records || 0,
          },
        ],
      },
    });

    const {
      totalDonationInPrimaryCurrency: receivedTotalDonationInPrimaryCurrency = {},
      totalProducts: receivedTotalProducts = 0,
    } = receivedUserInsights;
    dispatch({
      type: 'set_raised_details',
      payload: {
        amount: {
          label: i18n('label.summary-raised'),
          value: receivedTotalDonationInPrimaryCurrency.value || 0,
        },
        stats: [
          {
            label: i18n('label.summary-fundraisers'),
            value: receivedTotalProducts,
          },
        ],
      },
    });
  }, [timeFrame, insights]);

  const handleSelectionChange = (selectionIndex) => {
    setTimeFrame(summary.dateRanges[selectionIndex]);
  };

  const handleRetry = (e) => {
    e.preventDefault();
    if (isDonationError) {
      setIsDonationLoading(true);
      setIsDonationError(false);
    }
    if (isRaisedError) {
      setIsRaisedLoading(true);
      setIsRaisedError(false);
    }
  };

  const { donationDetails, raisedDetails } = summary;
  const isError = isDonationError || isRaisedError;

  return (
    <Sheet
      isOpen={isSheetOpen}
      onCloseComplete={closeSheet}
      title={summary.heading}
      hideTitle={true}
      data-nemo='giving-summary-details'
      skipFormFocus={true}
    >
      <div className={styles.container}>
        {isError ? (
          <GivingSummaryError
            heading={i18n('label.summary-error-heading')}
            message={i18n('label.summary-error-message')}
            retryMessage={i18n('label.summary-error-link')}
            onRetry={handleRetry}
          />
        ) : (
          <>
            <GivingSummaryHeader
              isEngaged={isEngaged}
              heading={summary.heading}
              subheading={summary.subheading}
              dropdownLabel={i18n('label.summary-dropdown')}
              dateRanges={summary.dateRanges}
              suggestedActions={summary.suggestedActions}
              selectedTimeFrame={timeFrame}
              onSelectionChange={handleSelectionChange}
              defaultFPTITags={defaultFPTITags}
              cdnHost={cdnHost}
              showPreviousYear={showPreviousYear}
              isLoading={isDonationLoading && isRaisedLoading}
            />
            <GivingSummarySection
              type='donated'
              amount={donationDetails.amount}
              stats={donationDetails.stats}
              suggestedActions={donationDetails.suggestedActions}
              defaultFPTITags={defaultFPTITags}
              isLoading={isDonationLoading}
            />
            {fundraiserSupport && (
              <GivingSummarySection
                type='raised'
                amount={raisedDetails.amount}
                stats={raisedDetails.stats}
                suggestedActions={raisedDetails.suggestedActions}
                defaultFPTITags={defaultFPTITags}
                isLoading={isRaisedLoading}
              />
            )}
            <CaptionText className={styles.caption}>
              {i18n('label.summary-disclaimer')}
            </CaptionText>
          </>
        )}
      </div>
    </Sheet>
  );
};

GivingSummary.propTypes = {
  isEngaged: PropTypes.bool,
  charitiesSupport: PropTypes.bool,
  fundraiserSupport: PropTypes.bool,
  preferredCharity: PropTypes.object,
  isCountrySetPreferredSupported: PropTypes.bool,
  isSheetOpen: PropTypes.bool,
  closeSheet: PropTypes.func,
  defaultFPTITags: PropTypes.object,
  i18n: PropTypes.any,
  csrf: PropTypes.string,
  cdnHost: PropTypes.string,
};

export default GivingSummary;
